import React, { useState, useEffect, useContext } from 'react';
import { 
    Card, CardBody, Button, Input, Label, FormGroup, Badge, 
    Row, Col, Alert, Modal, ModalHeader, ModalBody, ModalFooter 
} from 'reactstrap';
import NotificationContext from './NotificationContext';
import LoadingOverlay from 'react-loading-overlay';
import Success from './Success';

const EditConfigView = ({ configData, configIndex, onBack }) => {
    const { notificationConfig, updateNotificationConfig, createNotificationConfig, loading } = useContext(NotificationContext);
    const [editData, setEditData] = useState({
        groups: [],
        toEmails: [],
        ccEmails: []
    });
    const [errors, setErrors] = useState({});
    const [emailModal, setEmailModal] = useState({ show: false, type: '', newEmail: '', error: '' });
    const [isNewConfig, setIsNewConfig] = useState(false);
    const [isDefaultConfig, setIsDefaultConfig] = useState(false);
    const [isNewDefault, setIsNewDefault] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);

    const getGroupTypeLabel = () => 'Groups';
    const getGroupInputPlaceholder = () => 'Enter group identifier (e.g. customer ID, location ID)';

    const getModalTitle = (type) => type === 'groups' ? 'Group Identifier' : 'Email Address';

    const getAddButtonLabel = (type) => {
        switch(type) {
            case 'groups': return '+ Add Group';
            case 'to':     return '+ Add Custom TO Email';
            case 'cc':     return '+ Add Custom CC Email';
            default:       return '+ Add';
        }
    };

    useEffect(() => {
        if (configData) {
            setIsNewConfig(configIndex === -1);
            setIsNewDefault(configIndex === -2);
            setIsDefaultConfig(configData.grps && configData.grps.includes('DEFAULT'));
            
            if (configIndex === -1 || configIndex === -2) {
                setEditData({
                    groups: configData.grps || [],
                    toEmails: [],
                    ccEmails: []
                });
            } else {
                const splitEmails = (val) => {
                    if (!val) return [];
                    const arr = Array.isArray(val) ? val : [val];
                    return arr.flatMap(e => typeof e === 'string' ? e.split(',').map(s => s.trim()).filter(Boolean) : [e]);
                };
                setEditData({
                    groups: configData.grps || [],
                    toEmails: splitEmails(configData.emails?.to),
                    ccEmails: splitEmails(configData.emails?.cc)
                });
            }
        }
    }, [configData, configIndex]);

    const validateForm = () => {
        const newErrors = {};
        
        if (!isDefaultConfig) {
            if (editData.groups.length === 0) {
                newErrors.groups = 'At least one group is required';
            }
            
            if (editData.toEmails.length === 0) {
                newErrors.toEmails = 'At least one TO email is required';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        const groups = isDefaultConfig ? ['DEFAULT'] : editData.groups;
        const updatedConfig = {
            grps: groups,
            emails: {
                ...(editData.toEmails.length > 0 && {
                    to: editData.toEmails.join(',')
                }),
                ...(editData.ccEmails.length > 0 && {
                    cc: editData.ccEmails.join(',')
                })
            }
        };

        let success = false;
        const isFirstTimeSetup = !notificationConfig;
        if (isNewConfig) {
            const updatedEmailConfigs = [...(notificationConfig?.email_cfg || []), updatedConfig];
            const updatedNotificationConfig = { ...notificationConfig, email_cfg: updatedEmailConfigs };
            success = isFirstTimeSetup
                ? await createNotificationConfig(updatedNotificationConfig)
                : await updateNotificationConfig(updatedNotificationConfig);
        } else if (isNewDefault) {
            const updatedEmailConfigs = [...(notificationConfig?.email_cfg || []), updatedConfig];
            const updatedNotificationConfig = { ...notificationConfig, email_cfg: updatedEmailConfigs };
            success = isFirstTimeSetup
                ? await createNotificationConfig(updatedNotificationConfig)
                : await updateNotificationConfig(updatedNotificationConfig);
        } else {
            const updatedEmailConfigs = [...(notificationConfig?.email_cfg || [])];
            updatedEmailConfigs[configIndex] = updatedConfig;
            const updatedNotificationConfig = { ...notificationConfig, email_cfg: updatedEmailConfigs };
            success = await updateNotificationConfig(updatedNotificationConfig);
        }

        if (success) {
            setShowSuccess(true);
        }
    };

    const handleSuccessOk = () => {
        setShowSuccess(false);
        onBack();
    };

    const handleAddEmailModal = (type) => {
        setEmailModal({ show: true, type, newEmail: '' });
    };

    const handleEmailModalOk = () => {
        const email = emailModal.newEmail.trim();
        if (!email) return;
        let emailKey;
        if(emailModal.type === 'to'){
            emailKey = 'toEmails';
        } else if(emailModal.type === 'cc'){
            emailKey = 'ccEmails';
        } else if(emailModal.type === 'groups'){
            emailKey = 'groups';
        } else{
            return;
        }
        if(emailModal.type !== 'groups'){
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if(!emailRegex.test(email)){
                setEmailModal({ ...emailModal, error: 'Please enter a valid email address' });
                return;
            }
        }
        if(editData[emailKey].includes(email)){
            const itemType = emailModal.type === 'groups' ?
                'Group' : 'Email';
            setEmailModal({ ...emailModal, error: `${itemType} already exists` });
            return;
        }
        setEditData({
            ...editData, [emailKey]: [...editData[emailKey], email]
        });
        setEmailModal({ show: false, type: '', newEmail: '', error: '' });
    };
    
    const handleMacroToggle = (type, macro) => {
        const emailKey = type === 'to' ? 'toEmails' : 'ccEmails';
        const currentEmails = editData[emailKey];
        
        if (currentEmails.includes(macro)) {
            setEditData({...editData, [emailKey]: currentEmails.filter(email => email !== macro)});
        } else {
            setEditData({...editData, [emailKey]: [...currentEmails, macro]});
        }
    };

    const handleRemoveEmail = (type, index) => {
        let emailKey;
        if (type === 'to') {
            emailKey = 'toEmails';
        } else if (type === 'cc') {
            emailKey = 'ccEmails';
        } else if (type === 'groups') {
            emailKey = 'groups';
        } else {
            return;
        }

        setEditData({
            ...editData,
            [emailKey]: editData[emailKey].filter((_, i) => i !== index)
        });
    };

    const renderEmailSection = (type, title, emails) => (
        <FormGroup className="mb-4" style={{
            backgroundColor: '#f8f9fa',
            padding: '15px',
            borderRadius: '8px',
            border: '1px solid #e9ecef'
        }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <Label className="fw-bold" style={{  fontSize: '0.9rem', color: '#495057' }}>{title}</Label>
                <Button 
                    color="link" 
                    size="sm" 
                    onClick={() => handleAddEmailModal(type)} 
                    disabled={loading} 
                    className="p-0 text-success"
                    title={getAddButtonLabel(type)}
                    style={{ textDecoration: 'none', fontSize: '0.75rem' }}
                >
                    <span>{getAddButtonLabel(type)}</span>
                </Button>
            </div>

            {/* Macros */}
            {(type === 'to' || type === 'cc') && (
            <div className="mb-3">
                <div className="d-flex flex-wrap gap-3">
                    {[
                        { macro: '@ENTERED_USER@',        label: 'Entered User' },
                        { macro: '@OPERATIONS_USER@',     label: 'Operations User' },
                        { macro: '@ORDEREDBY_USER@',      label: 'Ordered By User' },
                        { macro: '@APPT_REQD_USR_EMAIL@', label: 'Appointment Requested User' },
                        { macro: '@CUST_SUPPORT_EMAIL@',  label: 'Customer Support' },
                        { macro: '@CUST_CONTACT_EMAIL@',  label: 'Customer Contact' },
                        { macro: '@MOTOR_CARRIER_EMAIL@', label: 'Motor Carrier Email' },
                    ].map(({ macro, label }) => {
                        const isChecked = emails.includes(macro);
                        return (
                            <div key={macro} className="d-flex align-items-center gap-2">
                                <input type="checkbox" checked={isChecked} onChange={() => handleMacroToggle(type, macro)} />
                                <label style={{ fontSize: '0.8rem', color: '#6c757d', fontWeight: '500' }}>{label}</label>
                            </div>
                        );
                    })}
                </div>
            </div>
            )}
            
            <div className="d-flex flex-wrap gap-2 mb-2">
                {emails.map((email, index) => (
                    <Badge key={index} color="secondary" className="d-flex align-items-center gap-2 p-1" style={{ fontSize: '0.75rem' }}>
                        {email}
                        <Button size="sm" color="link" className="p-0 text-white" style={{ fontSize: '12px', lineHeight: 1 }} onClick={() => handleRemoveEmail(type, index)} >
                            <i className="fas fa-times"></i>
                        </Button>
                    </Badge>
                ))}
            </div>
            
            {type === 'to' && errors.toEmails && (
                <div className="text-danger small">{errors.toEmails}</div>
            )}
            {type === 'groups' && errors.groups && (
                <div className="text-danger small">{errors.groups}</div>
            )}
        </FormGroup>
    );

    return (
        <LoadingOverlay active={loading} spinner>
            <Card className='p-4'>
                <CardBody>
                    <h5 className="text-primary mb-4">
                        {isDefaultConfig ? 'Edit DEFAULT Configuration' : 
                         (isNewConfig || isNewDefault) ? 'Add New Email Configuration' : 'Edit Email Configuration'}
                    </h5>
                    
                    {!isDefaultConfig && renderEmailSection('groups', getGroupTypeLabel(), editData.groups)}
                    {renderEmailSection('to', 'TO', editData.toEmails)}
                    {renderEmailSection('cc', 'CC', editData.ccEmails)}
                    
                    <div className="d-flex justify-content-end gap-2 mt-4">
                        <Button color="secondary" onClick={onBack} disabled={loading}>
                            Cancel
                        </Button>
                        <Button color="primary" onClick={handleSubmit} disabled={loading} >
                            {loading ? 'Submitting...' : 'Submit'}
                        </Button>
                    </div>
                </CardBody>

                <Modal isOpen={emailModal.show} toggle={() => setEmailModal({ show: false, type: '', newEmail: '', error: '' })} centered>
                    <ModalHeader toggle={() => setEmailModal({ show: false, type: '', newEmail: '', error: '' })}>
                        Enter {getModalTitle(emailModal.type)}
                    </ModalHeader>
                    <ModalBody>
                        <FormGroup>
                            <Label>{emailModal.type === 'groups' ? getModalTitle(emailModal.type) : 'Email Address'}</Label>
                            <Input
                                type={emailModal.type === 'groups' ? 'text' : 'email'}
                                value={emailModal.newEmail}
                                onChange={(e) => setEmailModal({ ...emailModal, newEmail: e.target.value, error: '' })}
                                onKeyDown={(e) => e.key === 'Enter' && handleEmailModalOk()}
                                placeholder={emailModal.type === 'groups' ? getGroupInputPlaceholder() : 'Enter email address'}
                                autoFocus
                            />
                            {emailModal.error && <div className="text-danger small mt-1">{emailModal.error}</div>}
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="primary" onClick={handleEmailModalOk}>OK</Button>
                        <Button color="secondary" onClick={() => setEmailModal({ show: false, type: '', newEmail: '', error: '' })}>Cancel</Button>
                    </ModalFooter>
                </Modal>
                
                <Modal isOpen={showSuccess} toggle={() => {}} centered>
                    <Success 
                        data={{ message: `Configuration ${(isNewConfig || isNewDefault) ? 'created' : 'updated'} successfully!` }}
                        cancel={handleSuccessOk}
                    />
                </Modal>
            </Card>
        </LoadingOverlay>
    );
};

export default EditConfigView;